import { openDB } from 'idb';
import { DATA_VERSION } from './version';

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team', 'canned_response'];
    this.accountId = accountId;
    this.db = null;
    this.dbOpening = null;
  }

  async initDb() {
    if (this.db) return this.db;
    if (!this.dbOpening) {
      this.dbOpening = this.openDatabase().finally(() => {
        this.dbOpening = null;
      });
    }
    return this.dbOpening;
  }

  openDatabase() {
    const dbName = `wasel-store-${this.accountId}`;
    return new Promise((resolve, reject) => {
      let settled = false;
      let timer;
      const fail = error => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(error);
      };
      timer = setTimeout(
        () => fail(new Error('Browser cache open timed out')),
        5000
      );
      const options = {
        upgrade(db) {
          // Existing databases already carry the stores added in earlier versions,
          // and createObjectStore throws on a name that is already taken.
          const createStore = (name, storeOptions) => {
            if (db.objectStoreNames.contains(name)) return;
            db.createObjectStore(name, storeOptions);
          };

          createStore('cache-keys');
          createStore('inbox', { keyPath: 'id' });
          createStore('label', { keyPath: 'id' });
          createStore('team', { keyPath: 'id' });
          createStore('canned_response', { keyPath: 'id' });
        },
        blocked() {
          fail(new Error('Browser cache upgrade is blocked'));
        },
        blocking: () => {
          this.db?.close();
          this.db = null;
        },
        terminated: () => {
          this.db = null;
        },
      };
      try {
        openDB(dbName, DATA_VERSION, options).then(db => {
          if (settled) {
            db.close();
            return;
          }
          settled = true;
          clearTimeout(timer);
          this.db = db;
          // Cache bookkeeping must not prevent a successful list request.
          try {
            const dbNames = JSON.parse(
              localStorage.getItem('wasel-idb-names') || '[]'
            );
            if (!dbNames.includes(dbName)) {
              dbNames.push(dbName);
              localStorage.setItem('wasel-idb-names', JSON.stringify(dbNames));
            }
          } catch {
            // Storage may be disabled or full; the cache is optional.
          }
          resolve(db);
        }, fail);
      } catch (error) {
        fail(error);
      }
    });
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    await this.db.clear(modelName);
    return this.push({ modelName, data });
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    await Promise.all(
      Object.entries(cacheKeys).map(([modelName, key]) =>
        this.db.put('cache-keys', key, modelName)
      )
    );
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
